import {
    Component,
    EventEmitter,
    Input,
    OnDestroy,
    OnInit,
    Output,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    ConfirmDialogButton,
    ConfirmDialogIcon,
    ConfirmDialogMode,
} from './models';

@Component({
    selector: 'app-confirm-dialog',
    templateUrl: './confirm-dialog.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false,
})
export class ConfirmDialogComponent implements OnInit, OnDestroy {
    @Input() public mode: ConfirmDialogMode = 'confirm';
    @Input() public text: string;
    @Input() public icon: ConfirmDialogIcon;

    // Confirm / Alert mode
    @Input() public confirmButtonText = '';
    @Input() public cancelButtonText = '';
    @Input() public showCancelButton = true;
    @Input() public showDenyButton = false;
    @Input() public denyButtonText = '';

    // Multi-button mode
    @Input() public buttons: ConfirmDialogButton[] = [];
    @Input() public timer = 0;
    @Input() public timerProgressBar = false;

    // Input mode
    @Input() public inputType: 'textarea' | 'range' | 'text';
    @Input() public inputLabel: string;
    @Input() public inputPlaceholder: string;
    @Input() public inputMin = 0;
    @Input() public inputMax = 100;
    @Input() public inputStep = 1;
    @Input() public inputValue: string | number = '';

    @Output() public result = new EventEmitter<any>();

    public timerProgress = 100;
    private timerInterval: ReturnType<typeof setInterval>;
    private timerTimeout: ReturnType<typeof setTimeout>;

    public get iconClass(): string {
        switch (this.icon) {
            case 'warning':
                return 'fa-light fa-triangle-exclamation text-amber-500';
            case 'error':
                return 'fa-light fa-circle-xmark text-rose-500';
            case 'info':
                return 'fa-light fa-circle-info text-blue-500';
            case 'success':
                return 'fa-light fa-circle-check text-emerald-500';
            default:
                return '';
        }
    }

    public get iconBgClass(): string {
        switch (this.icon) {
            case 'warning':
                return 'bg-amber-50 ring-amber-100';
            case 'error':
                return 'bg-rose-50 ring-rose-100';
            case 'info':
                return 'bg-blue-50 ring-blue-100';
            case 'success':
                return 'bg-emerald-50 ring-emerald-100';
            default:
                return '';
        }
    }

    public get timerBarColorClass(): string {
        switch (this.icon) {
            case 'warning':
                return 'bg-amber-400';
            case 'error':
                return 'bg-rose-400';
            case 'success':
                return 'bg-emerald-400';
            default:
                return 'bg-primary-400';
        }
    }

    public get hasContentAboveButtons(): boolean {
        return !!(this.icon || this.text || this.mode === 'input');
    }

    public ngOnInit(): void {
        if (this.timer > 0) {
            this.startTimer();
        }
    }

    public ngOnDestroy(): void {
        this.clearTimer();
    }

    public onConfirm(): void {
        this.clearTimer();
        if (this.mode === 'input') {
            this.result.emit({ type: 'confirm', value: this.inputValue });
        } else {
            this.result.emit({ type: 'confirm' });
        }
    }

    public onCancel(): void {
        this.clearTimer();
        this.result.emit({ type: 'cancel' });
    }

    public onDeny(): void {
        this.clearTimer();
        this.result.emit({ type: 'deny' });
    }

    public onButtonClick(key: string): void {
        this.clearTimer();
        this.result.emit({ type: 'button', key });
    }

    private startTimer(): void {
        const intervalMs = 50;
        const totalSteps = this.timer / intervalMs;
        let currentStep = 0;

        this.timerInterval = setInterval(() => {
            currentStep++;
            this.timerProgress = Math.max(
                0,
                100 - (currentStep / totalSteps) * 100
            );
        }, intervalMs);

        this.timerTimeout = setTimeout(() => {
            this.clearTimer();
            this.result.emit({ type: 'timer' });
        }, this.timer);
    }

    private clearTimer(): void {
        if (this.timerInterval) {
            clearInterval(this.timerInterval);
            this.timerInterval = null;
        }
        if (this.timerTimeout) {
            clearTimeout(this.timerTimeout);
            this.timerTimeout = null;
        }
    }
}
